import { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthProvider';
import { RestApi } from '../../../services/rest-api';
import { useNavigate, useParams } from 'react-router';
import { PageContainer } from '../../../components/PageContainer';
import { Accordion, Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { IField } from '../../../interfaces/field.interface';
import { AccountMaster, AgentMaster, fetchMasters, ItemMaster } from '../../../model/masters.model';
import { baseURL } from '../../../constants/config';
import { formatDate, numberDisplay, parseFixed } from '../../../services/formats';
import { useUtility } from '../../../contexts/UtilityContext';
import { Fields } from '../../../components/Fields';
import SaleOrderItemModal from './sale-order-item-modal';
import { fetchSaleOrderShow, SaleOrderAdvanceModel, SaleOrderItemModel, SaleOrderModel } from '../../../model/sale-order.model';
import Badge from '../../../components/Badge';
import SaleOrderAdvanceModal from './sale-order-advance-modal';

export const SaleOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const { authInfo } = useAuth();
  const params = useParams<{ id?: string }>();
  const { showLoader, hideLoader, toast } = useUtility();

  // LOCAL STATES
  const [selectedItem, setSelectedItem] = useState<SaleOrderItemModel | undefined>();
  const [editItemIdx, setEditItemIdx] = useState<number | null>();
  const [showItemModal, setItemModal] = useState(false);

  const [selectedAdvance, setSelectedAdvance] = useState<SaleOrderAdvanceModel | undefined>();
  const [editAdvanceIdx, setEditAdvanceIdx] = useState<number | null>();
  const [showAdvanceModal, setAdvanceModal] = useState(false);


  // MASTERS
  const [accounts, setAccounts] = useState<Array<AccountMaster>>([]);
  const [agents, setAgents] = useState<Array<AgentMaster>>([]);
  const [items, setItems] = useState<Array<ItemMaster>>([]);


  const fields: Array<IField> = [
    {
      label: "Order #",
      required: false,
      props: {
        name: "so_no",
        type: "text",
        inputMode: "text",
        placeholder: "Order #",
        readOnly: true,
      }
    }, {
      label: "Order Date",
      required: true,
      props: {
        name: "so_date",
        type: "date",
        inputMode: "text",
        placeholder: "Order Date"
      }
    }, {
      label: "Buyer",
      required: true,
      props: {
        name: "account",
        type: "typeahead",
        inputMode: "text",
        placeholder: "Buyer"
      },
      options: accounts,
    }, {
      label: "Agent / Broker",
      required: true,
      props: {
        name: "agent",
        type: "typeahead",
        inputMode: "text",
        placeholder: "Agent / Broker"
      },
      options: agents,
    }, {
      label: "Remarks",
      required: false,
      props: {
        name: "remarks",
        type: "text",
        inputMode: "text",
        placeholder: "Remarks"
      }
    }, {
      label: "Closed Status",
      required: false,
      props: {
        name: "closed_status",
        type: "switch",
        placeholder: "Closed Status"
      },
    }
  ];

  const { register, control, handleSubmit, reset, setValue, watch } = useForm<SaleOrderModel>({
    defaultValues: {
      so_date: new Date().toISOString().split('T')[0],
      items: [],
      advances: [],
    },
    mode: "onTouched",
    reValidateMode: "onChange"
  });

  const itemList = watch('items');
  const advances = watch('advances');
  const gst = watch('gst');
  const amt = watch('amt');
  const qty = watch('qty');
  const total_amt = watch('total_amt');

  useEffect(() => {
    async function fetchData() {
      try {
        showLoader();
        const masters = await fetchMasters('so', authInfo);
        setAccounts(masters?.accounts ?? []);
        setAgents(masters?.agents ?? []);
        setItems(masters?.items ?? []);
        if (params.id) {
          const res = await fetchSaleOrderShow(params.id, authInfo);
          reset({
            ...res,
            account: (masters?.accounts ?? []).find(ac => ac.id === res.ac_id),
            agent: (masters?.agents ?? []).find(ag => ag.id === res.agent_id),
            closed_status: res.closed_status === 'Y',
            items: res.items.map((itm: any) => ({
              ...itm,
              item: (masters?.items ?? []).find(i => i.id === itm.item_id)!,
            })),
          });
        }
      } catch (err) {
        console.log(err);
      } finally {
        hideLoader();
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id, authInfo, reset]);

  useEffect(() => {
    const qtySum = itemList.reduce((acc, cur) => (acc + parseFixed(cur.qty)), 0);
    const amtSum = itemList.reduce((acc, cur) => (acc + parseFixed(cur.amt)), 0);
    const gstAmt = parseFixed(gst * amtSum / 100);
    setValue('qty', qtySum);
    setValue('amt', amtSum);
    setValue('gst_amt', gstAmt);
    setValue('total_amt', amtSum + gstAmt);
  }, [itemList, gst, setValue]);


  const handleItemClose = () => {
    setItemModal(false);
    setEditItemIdx(null);
    setSelectedItem(undefined);
  };
  const handleItemShow = () => setItemModal(true);

  const handleEditItem = (idx: number) => {
    setEditItemIdx(idx);
    setSelectedItem(itemList[idx]);
    handleItemShow();
  };

  const handleRemoveItem = (idx: number) => {
    const items = [...itemList];
    items.splice(idx, 1);
    setValue('items', items);
  };

  const handleItemRow = (item: SaleOrderItemModel) => {
    let items = [...itemList];
    if (editItemIdx !== null && editItemIdx !== undefined) {
      items = items.map((dataItem, idx) => idx === editItemIdx ? item : dataItem);
      setEditItemIdx(null);
    } else {
      items.push(item);
    }
    setValue('items', items);
  };

  const handleAdvanceClose = () => {
    setAdvanceModal(false);
    setEditAdvanceIdx(null);
    setSelectedAdvance(undefined);
  };
  const handleAdvanceShow = () => setAdvanceModal(true);

  const handleEditAdvance = (idx: number) => {
    setEditAdvanceIdx(idx);
    setSelectedAdvance(advances[idx]);
    handleAdvanceShow();
  };

  const handleRemoveAdvance = (idx: number) => {
    const advs = [...advances];
    advs.splice(idx, 1);
    setValue('advances', advs);
  };

  const handleAdvanceRow = (adv: SaleOrderAdvanceModel) => {
    let advs = [...advances];
    if (editAdvanceIdx !== null && editAdvanceIdx !== undefined) {
      advs = advs.map((dataAdvance, idx) => idx === editAdvanceIdx ? adv : dataAdvance);
      setEditAdvanceIdx(null);
    } else {
      advs.push(adv);
    }
    setValue('advances', advs);
  };


  const onSubmit = handleSubmit(async (inputs) => {
    try {
      showLoader();
      await RestApi('post', params?.id ? 'updateSaleOrder' : 'createSaleOrder', {
        ...inputs,
        ...(params?.id ? { id: params.id } : {}),
        ac_id: inputs.account?.id,
        agent_id: inputs.agent?.id,
        items: inputs.items && inputs.items.map(item => ({
          item_id: item.item?.id,
          qty: item.qty,
          rate: item.rate,
          amt: item.amt,
        })),
      }, authInfo.user.authorization);
      navigate(`${baseURL}so-list`);
    } catch (message) {
      toast(message, 'danger');
    } finally {
      hideLoader();
    }
  });

  return (
    <PageContainer
      title="Sale Order Form"
      body={<>
        <Form onSubmit={onSubmit} noValidate>
          <div>
            <Fields fields={fields} control={control} register={register} />
          </div>
          <Row className='mb-3'>
            <Col className='text-start'>
              <strong>Order Items</strong>
            </Col>
            <Col className='text-end'>
              <Button size='sm' variant="secondary" onClick={() => {
                setSelectedItem(undefined);
                handleItemShow();
              }}>Add Item</Button>
            </Col>
          </Row>
          {itemList.length === 0 && <Alert variant='info'>No items added.</Alert>}
          <Accordion>
            {!!itemList && itemList.map((itm, idx) => (
              <Accordion.Item key={`item-${idx}`} eventKey={`${idx}`}>
                <Accordion.Header>{itm.item?.name}</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>Qty<br />{itm.qty}</Col>
                    <Col>Rate<br />{itm.rate}</Col>
                    <Col>Amount<br />{itm.amt}</Col>
                  </Row>
                  <div className='d-flex justify-content-end gap-2 mt-3'>
                    <Button size='sm' variant="info" onClick={() => handleEditItem(idx)}>Edit</Button>
                    <Button size='sm' variant="danger" onClick={() => handleRemoveItem(idx)}>Remove</Button>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
          <Row className='mt-4'>
            <Col className='text-start'>
              <Badge text={`Qnty: ${qty}`} color='info' />
            </Col>
            <Col className='text-end'>
              <Badge text={`Amount: ${amt}`} color='info' />
            </Col>
          </Row>
          <div className='mt-4'>
            <Fields fields={[
              {
                label: "GST",
                required: false,
                props: {
                  name: "gst",
                  type: "number",
                  inputMode: "number",
                  min: 0,
                  max: 100,
                  placeholder: "GST %",
                }
              }, {
                label: "GST Amount",
                required: false,
                props: {
                  name: "gst_amt",
                  type: "number",
                  inputMode: "number",
                  placeholder: "GST Amount"
                }
              }
            ]} control={control} register={register} />
          </div>
          <Row className='mt-4'>
            <Col className='text-end'>
              <Badge text={`Total Amount: ${total_amt}`} color='info' />
            </Col>
          </Row>

          <Row className='mt-4 mb-3'>
            <Col className='text-start'>
              <strong>Advance Payments</strong>
            </Col>
            <Col className='text-end'>
              <Button size='sm' variant="secondary" onClick={() => {
                setSelectedAdvance(undefined);
                handleAdvanceShow();
              }}>Add Advance</Button>
            </Col>
          </Row>
          {advances.length === 0 && <Alert variant='info'>No advance added.</Alert>}
          <Accordion>
            {!!advances && advances.map((itm, idx) => (
              <Accordion.Item key={`advance-${idx}`} eventKey={`${idx}`}>
                <Accordion.Header>
                  Payment : {numberDisplay(itm.amt)}
                </Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>Dated<br />{formatDate(itm.date)}</Col>
                    <Col>Narration<br />{itm.narration}</Col>
                    <Col>
                      <div className='d-flex justify-content-end gap-2 mt-3'>
                        <Button size='sm' variant="info" onClick={() => handleEditAdvance(idx)}>Edit</Button>
                        <Button size='sm' variant="danger" onClick={() => handleRemoveAdvance(idx)}>Remove</Button>
                      </div>
                    </Col>
                  </Row>
                  
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
          <Button className='w-100 mt-3' variant='primary' type="submit">Submit</Button>
        </Form>
        {showItemModal && <SaleOrderItemModal
          items={items}
          defaultData={selectedItem}
          handleSubmit={handleItemRow}
          handleClose={handleItemClose}
          show={showItemModal}
        />}

        {showAdvanceModal && <SaleOrderAdvanceModal
          defaultData={selectedAdvance}
          handleSubmit={handleAdvanceRow}
          handleClose={handleAdvanceClose}
          show={showAdvanceModal}
        />}
      </>
      }
    />
  );
};